import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import { getAllUsers } from "../services/adminService";

const ExpiredPlans = () => {
  const navigate = useNavigate();
  const [expiredUsers, setExpiredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: "",
    status: "all",
  });
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    fetchExpiredUsers();
  }, [filters]);

  const fetchExpiredUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllUsers(filters);

      // Filter only users with expired subscriptions
      const usersWithExpiredSubscriptions = data.users.filter((user) => {
        if (!user.UserSubscriptions || user.UserSubscriptions.length === 0) {
          return false;
        }

        // Filter only expired subscriptions
        const expiredSubscriptions = user.UserSubscriptions.filter(
          (subscription) => {
            return subscription.status === "expired";
          }
        );

        // Update the user object to only include expired subscriptions
        user.UserSubscriptions = expiredSubscriptions;

        // Return true only if user has at least one expired subscription
        return expiredSubscriptions.length > 0;
      });

      setExpiredUsers(usersWithExpiredSubscriptions);
      setPagination(data.pagination);
    } catch (error) {
      console.error("Error fetching expired users:", error);
      toast.error("Failed to fetch expired plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDaysExpired = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    const expiry = new Date(endDate);
    const diffTime = today.getTime() - expiry.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          backTo="/admin/dashboard"
          title="Expired Subscription Plans"
          subtitle="View all users whose subscriptions have expired"
        />

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Search Users
              </label>
              <input
                type="text"
                id="search"
                placeholder="Search by name, email, or plan..."
                value={filters.search}
                onChange={handleSearch}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Expired Plans Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-12">
              <LoadingSpinner size="lg" text="Loading expired plans..." />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subscription Period
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expired Since
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expiredUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-6 py-12 text-center text-gray-500"
                        >
                          No expired subscription plans found
                        </td>
                      </tr>
                    ) : (
                      expiredUsers.map((user) =>
                        user.UserSubscriptions.map((subscription) => {
                          const daysExpired = getDaysExpired(
                            subscription.currentPeriodEnd
                          );

                          return (
                            <tr
                              key={`${user.id}-${subscription.id}`}
                              className="hover:bg-gray-50 bg-red-50 border-l-4 border-l-red-400"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="ml-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {user.businessName}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {user.email}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                      {user.phone}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  <div className="font-medium">
                                    {subscription.subscriptionPlan?.name ||
                                      "Unknown Plan"}
                                  </div>
                                  <div className="text-gray-500">
                                    $
                                    {subscription.subscriptionPlan?.price ||
                                      "N/A"}
                                    /
                                    {subscription.subscriptionPlan?.interval ||
                                      "N/A"}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Groups:{" "}
                                    {subscription.subscriptionPlan?.maxGroups ||
                                      "N/A"}{" "}
                                    | Customers:{" "}
                                    {subscription.subscriptionPlan
                                      ?.maxCustomers || "N/A"}{" "}
                                    | Messages:{" "}
                                    {subscription.subscriptionPlan
                                      ?.maxMessages || "N/A"}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">
                                    Start:{" "}
                                    {formatDate(
                                      subscription.currentPeriodStart
                                    )}
                                  </div>
                                  <div className="text-red-600 font-medium">
                                    End:{" "}
                                    {formatDate(subscription.currentPeriodEnd)}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  {daysExpired} days ago
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  Expired
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total} results
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Summary Card */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mt-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Total Expired Plans
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {expiredUsers.reduce(
                    (total, user) => total + user.UserSubscriptions.length,
                    0
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Users whose subscriptions have expired
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpiredPlans;
